/**
 * AI Settings Hooks
 *
 * T012: TanStack Query hooks for AI configuration
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { aiConfigurationService } from '../services/ai-configuration-service';
import { getCurrentUserId } from '../lib/supabase';
import type { SaveAiSettingsInput } from '../types/ai-configuration';

export const aiSettingsKeys = {
  all: ['ai-settings'] as const,
  current: () => [...aiSettingsKeys.all, 'current'] as const,
  modelMappings: (provider: string) =>
    [...aiSettingsKeys.all, 'model-mappings', provider] as const,
};

export function useAiSettings() {
  return useQuery({
    queryKey: aiSettingsKeys.current(),
    queryFn: async () => {
      const userId = await getCurrentUserId();
      if (!userId) {
        throw new Error('User is not authenticated');
      }
      const result = await aiConfigurationService.getAiSettings(userId);
      if (result.error) {
        throw new Error(result.error.message);
      }
      return result.data!;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
}

export function useProviderModelMappings(provider: string) {
  return useQuery({
    queryKey: aiSettingsKeys.modelMappings(provider),
    queryFn: () => aiConfigurationService.getProviderModelMappings(provider),
    enabled: !!provider && provider !== 'none',
    staleTime: 10 * 60 * 1000,
  });
}

export function useSaveAiSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: SaveAiSettingsInput) => {
      const userId = await getCurrentUserId();
      if (!userId) {
        throw new Error('User is not authenticated');
      }
      const result = await aiConfigurationService.saveAiSettings(userId, input);
      if (result.error) {
        throw new Error(result.error.message);
      }
      return result.data!;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: aiSettingsKeys.current() });
    },
  });
}
